import type { Spritesheet, Texture } from "pixi.js";
import type { Log } from "@slot/presentation";
import type { AssetRegistry } from "../assets/AssetRegistry";
import type { ShellUiAssets } from "../assets/ShellUiAssets";

/** The `gameControlUI` frames the DOM chrome paints, by frame name, plus the desktop bar texture. */
export const CHROME_ART_FRAMES = [
  "spinBtnBack", "spinIcon", "slamIcon", "autoBetIcon", "roundBg", "fastIcon", "settingsIcon", "close", "closeRed",
  "buyBonusBtn", "plusCircle", "minusCircle", "betPlusIcon", "betMinusIcon", "betControlsBg",
  "autoAmountBg", "autoAmountBgBrown", "autoBetPanelBg", "switchOn", "switchOff", "switchHandle",
  "volume", "volumeLines", "musicIcon", "vibrationIcon", "historyIcon", "infoIcon", "homeIcon",
] as const;

export type ChromeArtFrame = (typeof CHROME_ART_FRAMES)[number] | "desktopBar";

/**
 * A title's control art (decision 49): an atlas whose frames, named as the shell's (`ChromeArtFrame`), replace the
 * shell's one by one; a frame it lacks stays the shell's. Each frame fills the box the chrome draws that frame in, so it
 * keeps the shell frame's proportions (its margins are the title's), unrotated and untrimmed: CSS shows the atlas
 * rectangle as it is. A replaced frame keeps the shell's paint (a glyph the chrome tints stays a glyph tinted by the
 * shell's colours, a state's included) unless `ownColours` names it: it then shows as drawn, and the state colour the
 * shell's tint carried there (the spin icon's feature mode, the slam face's red) is the title's art's to carry.
 */
export interface ChromeControlArtSpec {
  /** The bundle the atlas is in: one the boot loads (a core bundle or one of the title's `boot.extraBundles`). */
  bundle: string;
  /** The atlas's path in the bundle (its alias: bundle-relative, extensionless). */
  atlas: string;
  ownColours?: readonly ChromeArtFrame[];
  /**
   * The box the Buy Bonus label is fitted into, as fractions of the button's width and height, centred on it (decision
   * 52): for a `buyBonusBtn` that leaves the label less room than the whole button (a rim, a round face). Omitted, the
   * label is fitted to the whole button, as the shell's own art is drawn for.
   */
  buyBonusLabel?: ChromeLabelBox;
}

/** A control label's box: fractions of the control's width and height, centred on it (decision 52). */
export interface ChromeLabelBox {
  width: number;
  height: number;
}

/** One frame as CSS shows it: the image, the frame's rectangle in it, the image's pixel size. */
export interface ChromeArtSprite {
  url: string;
  x: number;
  y: number;
  width: number;
  height: number;
  imageWidth: number;
  imageHeight: number;
  /** A title's frame shown as drawn (decision 49): `artMask` paints it as an image, without the tint. */
  ownColours?: true;
  /** The box the control's label is fitted into, when the title's art leaves it less than the whole control (decision 52). */
  labelBox?: ChromeLabelBox;
}

/** The chrome's frames; a missing frame is absent, and the element paints nothing there. */
export type ChromeArt = Partial<Record<ChromeArtFrame, ChromeArtSprite>>;

/**
 * A title's control art as loaded: the atlas image's URL, its frames by name, the frames it drew in its own colours, the
 * Buy Bonus label's box on its button.
 */
export interface TitleControlArt {
  url: string;
  frames: Readonly<Record<string, Texture>>;
  ownColours: readonly ChromeArtFrame[];
  buyBonusLabel?: ChromeLabelBox;
}

/** A title frame's proportions may differ from the shell's by this much (2 %) before it is reported as stretched. */
const ASPECT_TOLERANCE = 0.02;

/** The title's atlas from the loaded bundles; null (logged) when it is not there, and the shell's art shows. */
export function loadTitleControlArt(assets: Pick<AssetRegistry, "getLoadedAssetOrNull" | "resolveUrl">, spec: ChromeControlArtSpec, log: Log): TitleControlArt | null {
  const sheet = assets.getLoadedAssetOrNull<Spritesheet>(spec.bundle, spec.atlas);
  const url = assets.resolveUrl(spec.bundle, spec.atlas);
  if (!sheet || !url) {
    log(`[ChromeArt] the title's control art "${spec.bundle}:${spec.atlas}" is not loaded: the shell's art shows`);
    return null;
  }
  return { url, frames: sheet.textures, ownColours: spec.ownColours ?? [], ...(spec.buyBonusLabel ? { buyBonusLabel: spec.buyBonusLabel } : {}) };
}

/**
 * The chrome art straight from the atlases: every frame is an atlas image positioned by CSS, so nothing is rasterised
 * at boot and the browser caches one image per atlas. A title's frames replace the shell's by name (decision 49); a
 * name the chrome does not paint, a frame whose proportions are not the shell's and an own-colour name the title has
 * no frame for are logged, since each would show nothing, stretched or tinted.
 */
export function buildChromeArt(assets: ShellUiAssets, title: TitleControlArt | null, log: Log): ChromeArt {
  const art: ChromeArt = {};
  if (assets.atlasUrl) {
    for (const name of CHROME_ART_FRAMES) {
      const texture = assets.frame(name);
      if (texture) art[name] = sprite(assets.atlasUrl, texture);
    }
  }
  if (assets.desktopBar && assets.desktopBarUrl) art.desktopBar = sprite(assets.desktopBarUrl, assets.desktopBar);
  if (title) overlayTitleArt(art, title, log);
  return art;
}

function overlayTitleArt(art: ChromeArt, title: TitleControlArt, log: Log): void {
  const own = new Set<string>(title.ownColours);
  for (const [name, texture] of Object.entries(title.frames)) {
    if (!isChromeArtFrame(name)) {
      log(`[ChromeArt] the title's control art has a frame "${name}" the chrome does not paint`);
      continue;
    }
    const replaced = art[name];
    const s = sprite(title.url, texture);
    if (replaced && Math.abs(s.width / s.height / (replaced.width / replaced.height) - 1) > ASPECT_TOLERANCE) {
      log(`[ChromeArt] the title's "${name}" is ${s.width}×${s.height}, the shell's ${replaced.width}×${replaced.height}: it fills the shell's box, so it shows stretched`);
    }
    art[name] = own.has(name) ? { ...s, ownColours: true } : s;
  }
  for (const name of own) {
    if (!(name in title.frames)) log(`[ChromeArt] the title names "${name}" in its own colours, but its control art has no such frame`);
  }
  if (title.buyBonusLabel) {
    const button = "buyBonusBtn" in title.frames ? art.buyBonusBtn : undefined;
    if (button) art.buyBonusBtn = { ...button, labelBox: title.buyBonusLabel };
    else log('[ChromeArt] the title boxes the Buy Bonus label, but its control art has no "buyBonusBtn" frame');
  }
}

function isChromeArtFrame(name: string): name is ChromeArtFrame {
  return name === "desktopBar" || (CHROME_ART_FRAMES as readonly string[]).includes(name);
}

function sprite(url: string, texture: Texture): ChromeArtSprite {
  return { url, x: texture.frame.x, y: texture.frame.y, width: texture.frame.width, height: texture.frame.height, imageWidth: texture.source.pixelWidth, imageHeight: texture.source.pixelHeight };
}

/**
 * The image sized so the frame fills the box, and shifted so the frame's corner sits at the box's:
 * a background (or mask) size in percent of the box, and a position in percent of the free travel.
 */
function placement(s: ChromeArtSprite): { size: string; position: string } {
  const percent = (offset: number, image: number, frame: number): string => (image === frame ? "0%" : `${(offset / (image - frame)) * 100}%`);
  return {
    size: `${(s.imageWidth / s.width) * 100}% ${(s.imageHeight / s.height) * 100}%`,
    position: `${percent(s.x, s.imageWidth, s.width)} ${percent(s.y, s.imageHeight, s.height)}`,
  };
}

/** `background-image` (and its placement) for a frame, or nothing while the art is missing. */
export function artImage(art: ChromeArt, frame: ChromeArtFrame): string {
  const s = art[frame];
  if (!s) return "";
  const p = placement(s);
  return `background-image: url("${s.url}"); background-size: ${p.size}; background-position: ${p.position}`;
}

/**
 * A tinted icon: the frame as a mask over a colour (the sprite tint on a white frame). A title's frame in its own
 * colours (decision 49) is the frame as an image instead, the colour unused.
 */
export function artMask(art: ChromeArt, frame: ChromeArtFrame, color: string): string {
  const s = art[frame];
  if (!s) return "";
  if (s.ownColours) return `${artImage(art, frame)}; background-repeat: no-repeat`;
  const p = placement(s);
  return `-webkit-mask-image: url("${s.url}"); -webkit-mask-size: ${p.size}; -webkit-mask-position: ${p.position}; mask-image: url("${s.url}"); mask-size: ${p.size}; mask-position: ${p.position}; background-color: ${color}`;
}
